import { useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import ImageWithFallback from './ImageWithFallback';
import { listProducts } from '../utils/queries';

/**
 * Live product search/typeahead -- types into a search box, results appear
 * in a dropdown below, clicking one calls onSelect(product) and clears the
 * box. Used by KitItemsTab to find products to add to a kit; excludes other
 * kits via productType: 'simple' so kits can't bundle kits.
 */
export default function ProductSearchInput({ storeId, onSelect, excludeProductIds = [], placeholder = 'Search products...' }) {
	const [query, setQuery] = useState('');
	const [results, setResults] = useState([]);
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const debounceRef = useRef(null);
	const containerRef = useRef(null);

	useEffect(() => {
		if (!query) {
			setResults([]);
			return;
		}
		setLoading(true);
		clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => {
			listProducts({ search: query, storeId, productType: 'simple', limit: 10 }).then((result) => {
				setLoading(false);
				if (result?.success) {
					setResults(result.data.products.filter((p) => !excludeProductIds.includes(p.productId)));
					setOpen(true);
				}
			});
		}, 250);
		return () => clearTimeout(debounceRef.current);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [query, storeId]);

	useEffect(() => {
		const handleClickOutside = (e) => {
			if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const handleSelect = (product) => {
		onSelect(product);
		setQuery('');
		setResults([]);
		setOpen(false);
	};

	return (
		<div className='relative' ref={containerRef}>
			<div className='relative max-w-sm'>
				<Search className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400' />
				<input
					className='w-52 form-input pl-9'
					placeholder={placeholder}
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					onFocus={() => results.length > 0 && setOpen(true)}
				/>
			</div>
			{open && (
				<div className='absolute z-10 mt-1 w-full max-w-sm bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-y-auto'>
					{loading && <div className='px-3 py-2 text-sm text-gray-400'>Searching...</div>}
					{!loading && results.length === 0 && (
						<div className='px-3 py-2 text-sm text-gray-400'>No products found</div>
					)}
					{!loading &&
						results.map((product) => (
							<button
								key={product.productId}
								type='button'
								className='w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50'
								onClick={() => handleSelect(product)}
							>
								<div className='size-8 shrink-0 rounded bg-gray-100 overflow-hidden'>
									<ImageWithFallback
										src={product.imageUrl}
										alt={product.name}
										className='size-full object-cover'
										fallbackClassName='size-full'
									/>
								</div>
								<div className='min-w-0'>
									<p className='text-sm text-black truncate'>{product.name}</p>
									<p className='text-xs text-gray-400 truncate'>{product.productNumber}</p>
								</div>
							</button>
						))}
				</div>
			)}
		</div>
	);
}
